var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import * as PIXI from "pixi.js";
import { a as assignGlobalSingleton } from "../global-33d5eebf.js";
class PixiSpriteLoader {
  constructor() {
    __publicField(this, "pixiSpriteLibrary", /* @__PURE__ */ new Map());
    __publicField(this, "spriteLibrary", /* @__PURE__ */ new Map());
    __publicField(this, "getStaticFrame", (definition) => ({
      default: {
        frame: {
          x: definition.sourcePos.x,
          y: definition.sourcePos.y,
          w: definition.spriteSize.x,
          h: definition.spriteSize.y
        }
      }
    }));
    __publicField(this, "getAnimationFrameName", (animationName, idx) => `${animationName}_${idx}`);
    __publicField(this, "getMetaFromDefinition", (definition) => ({
      meta: {
        image: definition.imageName,
        format: "RGBA8888",
        size: { w: definition.imageSize.x, h: definition.imageSize.y },
        scale: definition.scale ?? "1"
      }
    }));
    __publicField(this, "getAnimationPos", (idx, sourcePos, spriteSize, offset) => sourcePos + idx * spriteSize + idx * offset);
  }
  async loadSprite(name, assetPath, sprites, subSprites) {
    var _a;
    if (!((_a = sprites[0]) == null ? void 0 : _a.image)) {
      console.error(name, assetPath, sprites, subSprites);
    }
    const image = sprites[0].image;
    const spriteData = {
      frames: {},
      meta: {
        image: assetPath,
        format: "RGBA8888",
        size: { w: image.width, h: image.height },
        scale: "1"
      },
      animations: {
        [name]: sprites.map((sprite) => sprite.name + "_" + sprite.frameNumber)
        //array of frames by name
      }
    };
    sprites.forEach((sprite) => {
      spriteData.frames[sprite.name + "_" + sprite.frameNumber] = {
        frame: { x: sprite.x, y: sprite.y, w: sprite.width, h: sprite.height },
        sourceSize: { w: sprite.width, h: sprite.height },
        spriteSourceSize: { x: 0, y: 0, w: sprite.width, h: sprite.height }
      };
    });
    if (subSprites) {
      Object.keys(subSprites).forEach((key) => {
        spriteData.animations[key] = subSprites[key].map((sprite) => {
          return name + "_" + sprite.frameNumber;
        });
      });
    }
    const spritesheet = new PIXI.Spritesheet(
      PIXI.BaseTexture.from(spriteData.meta.image),
      spriteData
    );
    await spritesheet.parse();
    Object.entries(spriteData.animations).forEach(([key, value]) => {
      spritesheet.animations[key] = value.map((animation) => {
        return spritesheet.textures[animation];
      });
    });
    this.pixiSpriteLibrary.set(name, spritesheet);
  }
  async preloadSpriteFromOptions(name, path, options) {
    const spriteDefinition = this.spriteOptionsToDefinition(options, path, name);
    const spriteSheet = this.preloadSprite(spriteDefinition);
    await spriteSheet.parse();
    this.pixiSpriteLibrary.set(name, spriteSheet);
  }
  spriteOptionsToDefinition(options, path, name) {
    const spriteTexture = PIXI.BaseTexture.from(path);
    const animations = [];
    if (options.structure !== void 0) {
      const aliases = options.aliases ?? [name];
      const structure = options.structure;
      aliases.forEach((alias, aliasIdx) => {
        let startFrame = 0;
        structure.forEach((structure2) => {
          const structureNames = Array.isArray(structure2.name) ? structure2.name : [structure2.name];
          const animationNames = structureNames.map((structureName) => `${alias}_${structureName}`);
          const animationDefinitions = animationNames.map((animationName) => {
            console.log(
              `${animationName} start ${startFrame} width ${options.width} = ${startFrame * (options.width ?? 1)}`
            );
            const clonePartial = {
              name: animationName,
              startPos: {
                x: startFrame * (options.width ?? 32),
                y: aliasIdx * (options.height ?? 32)
              },
              numFrames: structure2.frames
            };
            clonePartial.name = animationName;
            return clonePartial;
          });
          startFrame += structure2.frames;
          animations.push(...animationDefinitions);
        });
      });
    }
    const t = {
      imageName: path,
      spriteName: name,
      imageSize: {
        x: spriteTexture.width,
        y: spriteTexture.height
      },
      spriteSize: {
        x: options.width ?? spriteTexture.width,
        y: options.height ?? spriteTexture.height
      },
      sourcePos: {
        x: options.margin ?? 0,
        y: options.margin ?? 0
      },
      animations,
      frameRate: 5e-4,
      offsets: { x: 0, y: 0 }
    };
    return t;
  }
  static getInstance() {
    return assignGlobalSingleton("PixiSpriteLoader", () => new PixiSpriteLoader());
  }
  getFramesForAnimations(definition) {
    const frames = {};
    const animationsList = {};
    for (let i = 0; i < definition.animations.length; i++) {
      animationsList[definition.animations[i].name] = [];
      for (let j = 0; j < definition.animations[i].numFrames; j++) {
        const animationName = definition.animations[i].name;
        animationsList[animationName].push(this.getAnimationFrameName(animationName, j));
        const frame = this.getFrameFromAnimation(j, definition, definition.animations[i]);
        Object.assign(frames, frame);
      }
    }
    return {
      frames,
      animations: animationsList
    };
  }
  preloadSprite(definition) {
    const animationData = this.getFramesForAnimations(definition);
    const meta = this.getMetaFromDefinition(definition);
    const atlasData = {
      ...animationData,
      ...meta
    };
    const spritesheet = new PIXI.Spritesheet(PIXI.BaseTexture.from(atlasData.meta.image), atlasData);
    return spritesheet;
  }
  getFrameFromAnimation(idx, definition, animation) {
    const { spriteSize, sourcePos, offsets } = definition;
    return {
      [`${this.getAnimationFrameName(animation.name, idx)}`]: {
        frame: {
          x: this.getAnimationPos(idx, sourcePos.x, spriteSize.x, (offsets == null ? void 0 : offsets.x) || 0),
          y: sourcePos.y,
          w: spriteSize.x,
          h: spriteSize.y
        },
        spriteSourceSize: { x: 0, y: 0, w: spriteSize.x, h: spriteSize.y },
        spriteSize: { w: spriteSize.x, h: spriteSize.y }
      }
    };
  }
}
export {
  PixiSpriteLoader
};
//# sourceMappingURL=PixiSpriteLoader.js.map
